/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Inject,
  OnInit,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { IconRegistryInitService } from './services/icon-registry-init-service';
import { ICON_REGISTRY_CONFIG } from './icon-registry-config';
import { combineLatest, Subject } from 'rxjs';
import { delay, filter, retry, takeUntil, tap } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { skipEmpty } from './shared/utils/skip-empty.operator';
import { SidebarService } from './layout/sidebar/services/sidebar.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { appModuleActions } from './views/users-management/store/action-types';
import { DOCUMENT } from '@angular/common';
import {
  AppInfoService,
  FacAuthService,
  FacQibAuthService,
  QibSigninService,
  TokenResponse,
  UserInfo,
  SubscriptionsInfo,
  ProductInfo,
} from '@foglight/angular-common';
import { FacNavbarService } from './layout/navbar/navbar.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { IdleUserService } from './services/idle-user/idle-user.service';
import { AppStatusService } from './services/app-status/app-status.service';
import {
  serverInfoSelector,
  subscriptionInfo,
  userInfo,
} from './store/app.selectors';
import { isEmpty } from 'lodash-es';

@Component({
  selector: 'fc-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  title = 'Foglight';
  isKeepAliveIntervalExists = false;
  isLoading = false;
  lightTheme = 'light-theme';
  darkTheme = 'dark-theme';
  currentTheme = this.lightTheme;
  jwtTokenInterval: number | undefined = undefined;
  qibTokenInterval: number | undefined = undefined;

  worker!: Worker;

  @HostListener('document:keyup', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    const focusableEle = document.querySelectorAll(
      'button:not([disabled]):not([tabindex="-1"]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]):not(div.fac-tree-navigation-invisible-section > cdk-nested-tree-node):not(div.mat-sort-header-container), details:not([disabled]), tr:not(.secondary-table-height):not(.fac-table-header-row)',
    );

    // find active element
    let elementIndex = 0;
    let element: HTMLElement = {} as HTMLElement;

    for (let i = 0; i < focusableEle.length - 1; i++) {
      if (focusableEle[i] == document.activeElement) {
        elementIndex = i;
        element = focusableEle[i] as HTMLElement;
      }
    }

    //Change keyboard key press behaviour to allow accessibility through keyboard only
    if (
      element?.tagName != 'TEXTAREA' ||
      ((element as HTMLInputElement)?.selectionStart ==
        (element as HTMLInputElement)?.value?.length &&
        event.key == 'ArrowRight') ||
      ((element as HTMLInputElement)?.selectionStart == 0 &&
        event.key == 'ArrowLeft')
    ) {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          break;
        case 'ArrowUp':
          if (
            elementIndex <= 2 ||
            focusableEle[elementIndex].classList.contains(
              'mat-autocomplete-trigger',
            )
          ) {
            break;
          }
          (focusableEle[elementIndex - 1] as HTMLElement).focus();
          break;
        case 'ArrowRight':
          event.preventDefault();
          break;
        case 'ArrowDown':
          //if (event.target['tagName'].toLowerCase() !== 'input') {
          if (
            elementIndex === focusableEle.length - 1 ||
            focusableEle[elementIndex].classList.contains(
              'mat-autocomplete-trigger',
            )
          ) {
            break;
          }
          (focusableEle[elementIndex + 1] as HTMLElement).focus();
          break;
        case 'Enter':
          if (
            document.activeElement?.nextElementSibling?.classList.contains(
              'side-menu',
            ) ||
            this.document.activeElement?.tagName.toLowerCase() == 'button'
          ) {
            break;
          }
        case ' ':
          if (this.document.activeElement?.getAttribute('type') !== 'text') {
            (document.activeElement as HTMLElement).click();
            break;
          }
          break;
      }
    }
  }

  constructor(
    iconRegistryInit: IconRegistryInitService,
    private router: Router,
    private sidebarService: SidebarService,
    private http: HttpClient,
    private store: Store,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private navbarService: FacNavbarService,
    private authService: FacAuthService,
    private idleUserService: IdleUserService,
    private qibAuthService: FacQibAuthService,
    private appInfoService: AppInfoService,
    private qibSigninService: QibSigninService,
    private appStatusService: AppStatusService
  ) {
    // apply the registry to icon registry service for dynamic svg icon loading
    iconRegistryInit.init(ICON_REGISTRY_CONFIG);
  }

  ngOnInit(): void {
    const persistedTheme = localStorage.getItem('theme') === this.darkTheme
      ? this.darkTheme
      : this.lightTheme;
    this.setThemeClass(persistedTheme);

    if (window.self !== window.top) {
      this.isLoading = true;
      window.top?.postMessage({ url: window.location.href }, '*');
    } else {
      window.addEventListener('message', this.handleMessage.bind(this));
    }

    // Handle app info - start

    this.handleProductInfo();
    this.handleUserInfo();
    this.handleSubscriptionsInfo();

    setTimeout(() => {
      this.store.dispatch(appModuleActions.getServerInfo());
      this.store.dispatch(appModuleActions.getUserInfo());
    }, 300);

    // Handle app info - end

    combineLatest([
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd && event.url === '/'),
      ),
      this.sidebarService.redirectLink$.pipe(skipEmpty()),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([nav, link]) => {
        this.router.navigateByUrl(link);
      });

    this.keepAliveInit();
    this.checkKeepAliveQibToken();
    this.getSelectedTheme();

    this.worker = new Worker(new URL('./app.worker', import.meta.url));
    this.worker.onmessage = ({ data }) => this.keepAliveAuiAPI(data);
  }

  ngOnDestroy(): void {
    this.idleUserService.stopWatching();

    if (this.isKeepAliveIntervalExists) {
      this.isKeepAliveIntervalExists = false;
      clearInterval(this.jwtTokenInterval);
      this.jwtTokenInterval = undefined;
      this.worker.postMessage({ killInterval: true });
    }

    this.qibTokenInterval = undefined;
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleMessage(event: MessageEvent): void {
    if (event.data && event.data.url) {
      const fullUrl = event.data.url;
      // Extract the part of the URL after 'aui/'
      const extractedPath = this.extractPath(fullUrl);
      // Ensure the extracted path is correct and navigate if path is AUI page
      if (extractedPath) {
        this.router.navigateByUrl(`/${extractedPath}`);
        this.isLoading = false;
      }
    }
  }

  extractPath(fullUrl: string): string | null {
    const basePath = '/aui/';
    const index = fullUrl.indexOf(basePath);
    if (index !== -1) {
      return fullUrl.substring(index + basePath.length);
    }
    return null;
  }

  keepAliveAuiAPI(bookId: string): void {
    const sub = this.http
      .get<any>(`/console/events${bookId}`, { observe: 'response' })
      .pipe(retry(3), delay(100))
      .subscribe(
        () => {
          sub.unsubscribe();
        },
        (error: HttpErrorResponse) => {
          if (error.status === 410 || error.status === 502) {
            this.authService.logout();
          }

          sub.unsubscribe();

          console.log('Keep alive error');
        },
      );
  }

  refreshJwtToken(): void {
    this.authService
      .renewJwtToken()
      .pipe(
        tap((it) => {
          this.authService.storeAccessAndRefreshToken(
            it?.access_token,
            it?.refresh_token,
          );
        }),
      )
      .subscribe();
  }

  checkKeepAliveQibToken(): void {
    if (!this.qibTokenInterval) {
      this.qibTokenInterval = window.setInterval(() => {
        this.refreshQibJwtToken();
      }, 240000);
    }
  }

  refreshQibJwtToken(): void {
    if (!isEmpty(this.qibAuthService.qibAccessToken)) {
      this.qibAuthService
        .renewQibToken()
        .pipe(
          tap((tokenResponse: TokenResponse) => {
            this.qibAuthService.qibAccessToken = tokenResponse.access_token;
            this.qibAuthService.qibRefreshToken = tokenResponse.refresh_token;
            this.qibAuthService.qibId_token = tokenResponse.id_token;
          }),
        )
        .subscribe();
    }
  }

  getSelectedTheme(): void {
    this.navbarService.userSelectedTheme$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        localStorage.setItem('theme', res);
        this.setThemeClass(res);
      });
  }

  setThemeClass(theme: string): void {
    if (theme == this.darkTheme) {
      this.renderer.removeClass(this.document.body, this.lightTheme);
      this.renderer.addClass(this.document.body, this.darkTheme);
    } else {
      this.renderer.removeClass(this.document.body, this.darkTheme);
      this.renderer.addClass(this.document.body, this.lightTheme);
    }
  }

  private keepAliveInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((res: NavigationEnd) => {
        if (res.url.indexOf('auth') === -1) {
          if (!this.jwtTokenInterval) {
            this.jwtTokenInterval = window.setInterval(() => {
              this.refreshJwtToken();
            }, 240000);
          }
          if (!this.isKeepAliveIntervalExists) {
            this.isKeepAliveIntervalExists = true;
            this.http
              .get<any>('/console/page', { observe: 'response' })
              .subscribe(
                (resp) => {
                  const location = resp.headers.get('Location') || '';
                  this.postLocationToWebWorker(location);
                },
                (e) => {
                  let url = e.url + '';
                  this.postLocationToWebWorker(url);
                },
              );
          }
        }
      });
  }

  private postLocationToWebWorker(url: string): void {
    const book = url.slice(url.lastIndexOf('/'));
    this.worker.postMessage({ book, killInterval: false });
  }

  private handleProductInfo(): void {
    this.store
      .select(serverInfoSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((productInfo: ProductInfo) => {
        if (productInfo.isCloud) {
          this.setupCloudEnvironment();
        }

        if (productInfo?.chat) {
          this.qibSigninService.setQibAuthConfig(productInfo?.chat);

          this.qibAuthService.qibEndpoint = productInfo?.chat.qibEndpoint || '';
        }

        this.appInfoService.productInfo = productInfo;
      });
  }

  private handleUserInfo(): void {
    this.store
      .select(userInfo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((userInfo: UserInfo) => {
        this.appInfoService.userInfo = userInfo;
      });
  }

  private handleSubscriptionsInfo(): void {
    this.store
      .select(subscriptionInfo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((subscriptionsInfo: SubscriptionsInfo[]) => {
        this.appInfoService.subscriptionInfo = [...subscriptionsInfo];
      });
  }

  private setupCloudEnvironment(): void {
    this.store.dispatch(appModuleActions.getSubscriptionInfo());
    this.appStatusService.initializeAppStatus();
  }
}
